<template>
  <div>
    <!-- DOMAIN PAGE -->
    <transition @enter="initial">
      <div v-if="domain">
        <!-- TOP SECTION -->
        <section class="bg-tertiary ot-section">
          <div class="center">
            <div class="md:w-3/5">
              <div class="text-white uppercase flex items-center">
                Domain

                <SelectCustom
                  :value="domainId"
                  @input="routeDomain"
                  :options="domainsOptions"
                  class="inline-block md:hidden ml-2"
                  select-class="border-tertiary hover:bg-tertiary hover:bg-white focus:ring-tertiary"
                />
              </div>
              <h1 class="text-3xl mb-4 ot-anim font-title tracking-tight">
                {{ domain.name }}
              </h1>

              <div class="flex mb-4 max-w-xl">
                <div class="ot-anim ot-line">
                  {{ domain.text }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="ot-section">
          <div class="center ">
            <p class="mb-2 ot-anim ot-line">
              This dashboard is a curated list of national policies relevant to adolescents under the 9 thematic domains and whether countries have enacted each policy. Adolescent-focused policies demonstrate commitment to achieving adolescent wellbeing and are often the first step in promoting change.
            </p>            

            <div class="mt-4 ot-anim">
              <label for="region" class="mr-2">Select a region:</label>

              <SelectCustom
                :value="regionId"
                @input="routeRegion"
                :options="regions"
                label="region"
                class="w-full sm:w-auto max-w-sm"
              />
            </div>

            <div class="ot-card-title ot-anim mt-10">
              <h3 class="text-3xl font-bold ot-anim ot-line font-title">
                {{ region ? region.name : "All regions" }}
              </h3>
            </div>
          </div>
        </section>

        <section class="bg-white ot-section">
          <div class="center ">
            <div class="ot-card ot-anim ">
              <div class="flex flex-col md:flex-row justify-between mb-4">
                <div class="ot-line text-gray-500 text-sm">
                  <p>
                    <font-awesome-icon
                      :icon="['fas', 'mouse-pointer']"
                      class="mr-1"
                    />
                    Place your mouse over the cells to get more information
                  </p>
                  <p>
                    <font-awesome-icon
                      :icon="['far', 'hand-pointer']"
                      class="mr-1"
                    />
                    Click on a policy to get more information
                  </p>
                </div>

                <div class="self-end md:self-start mt-4 md:-mt-0">
                  <DownloadButton :href="policyDataLink" />
                </div>
              </div>
              <div class="ot-anim overflow-x-scroll">
                <HeatMap
                  :data="heatmapData"
                  :color-scale="heatmapColorScale"
                  :categories="heatmapCategories"
                  :showIcons="true"
                  @select="showDetails"
                  @hover="
                    $event ? showHeatmapTooltip($event) : hideHeatmapTooltip()
                  "
                  @hoverCat="showHeaderTooltip"
                  :rotate-headers="true"
                  :rotate-right="true"
                  row-class="cursor-pointer"
                  :wide="true"
                  class="mt-36"
                />
              </div>
              <!-- HEATMAP LEGEND -->
              <div class="mt-4 text-xs flex justify-end">
                <div class="flex flex-col space-y-0.5">
                  <div>
                    <font-awesome-icon
                      :icon="['fas', 'square']"
                      :style="{ color: heatmapColorScale(1) }"
                    />
                    Policy is present
                  </div>

                  <div>
                    <font-awesome-icon
                      :icon="['fas', 'square']"
                      :style="{ color: heatmapColorScale(0) }"
                    />
                    Policy is not present
                  </div>
                  <div>
                    <font-awesome-icon
                      :icon="['fas', 'square']"
                      :style="{ color: colors['na'] }"
                    />
                    No data available
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <!-- NOTES -->
        <section class="ot-section">
            <div class="text-sm mt-12 center">
              <h4 class="mb-2 font-medium ot-anim">Notes</h4>
            <p class="mb-2 ot-anim ot-line text-gray-500">
              This is not an exhaustive list of all policies that impact the lives of adolescents. Rather, the policies included here fall under the 9 domains of the A3 and aim to start the conversation around promoting adolescent health and wellbeing through policy change.
            </p>
            <p class="mb-2 ot-anim ot-line text-gray-500">
              The information shown here represents policies at the national level. The dashboard does not take into account local policies, court decisions, enforcement of policies, or overall social climate and may not reflect recent policy changes. A label of "No Data Available" indicates that no data was available or that a determination on a policy was unable to be made.
            </p>

            <p class="mb-2 ot-anim ot-line text-gray-500">
              This dashboard was last updated in 2021. If you are aware of a recent policy update that affects this dashboard, please let us know at <a href="mailto:a3@popcouncil.org" class="font-bold">a3@popcouncil.org</a>
              <!-- Learn how to use the A3 policy checklist
              <a
                href="https://www.youtube.com/user/PopulationCouncil/playlists"
                target="_blank"
                class="underline text-primary"
                >here</a
              >. -->
            </p>


            </div>
        </section>
      </div>
    </transition>

    <!--TOOLTIP -->
    <VTooltip
      :show="heatmapTooltip.show"
      :target="heatmapTooltip.target"
      class="z-30 w-72"
    >
      <div class="flex items-start">
        <CountryFlag
          :iso="heatmapTooltip.iso"
          class="w-7"
          v-if="heatmapTooltip.iso"
        />
        <div class="ml-4 space-y-2">
          <div class="font-bold text-base mt-0.5">
            {{ heatmapTooltip.title }}
          </div>
          <div class="">{{ heatmapTooltip.subtitle }}</div>
          <hr class="my-2" />
          <div class="text-base">{{ heatmapTooltip.value }}</div>
        </div>
      </div>
    </VTooltip>
    <!-- HEADER TOOLTIP -->
    <VTooltip
      :show="headerTooltip.show"
      :target="headerTooltip.target"
      class="z-30"
    >
      <div class="flex items-start">
        <CountryFlag
          :iso="headerTooltip.iso"
          class="w-7"
          v-if="headerTooltip.iso"
        />
        <div class="ml-4 space-y-2">
          <div class="font-bold text-base mt-0.5">
            {{ headerTooltip.title }}
          </div>
        </div>
      </div>
    </VTooltip>
    <!-- MODAL -->
    <TwModal :is-open="showModal" @close="showModal = false">
      <span v-if="policy" slot="header">{{ policy.name }}</span>

      <div v-if="policy" slot="body">
        <div class="relative mb-4">
          <WorldMap
            v-if="showModal"
            ref="worldMap"
            :data="mapData"
            :colors="heatmapColorScale"
            :no-data-color="noColorData"
            :fitBoundsToData="fitBoundsToData"
            :adjust-size="true"
            class="h-48 sm:h-72 xl:h-96"
            :scale="mapZoom"
            :use-bg-pattern="true"
            @zoom="setCurrentZoom"
            @hover="toggleTooltip"
          />

          <!-- WORLDMAP TOOLTIP -->
          <VTooltip
            :show="worldMapTooltip.show"
            :followMouse="true"
            class="z-30 w-80"
          >
            <div class="flex items-start">
              <CountryFlag
                :iso="worldMapTooltip.iso"
                class="w-7"
                v-if="worldMapTooltip.iso"
              />
              <div class="ml-4 overflow-x-hidden space-y-2">
                <div class="font-bold text-base mt-0.5">
                  {{ worldMapTooltip.title }}
                </div>
                <div class="">{{ worldMapTooltip.subtitle }}</div>
                <hr class="my-2" />
                <div>
                  <!-- <div class="text-gray-500">
                    Percentage of adolescents
                  </div> -->
                  <div class="font-bold text-lg">
                    {{ worldMapTooltip.value }}
                  </div>
                </div>

                <div>
                  <div class="text-gray-500">
                    Number of adolescents
                  </div>
                  {{ worldMapTooltip.number | number(0) }}
                </div>
              </div>
            </div>
          </VTooltip>

          <!-- MAP BUTTONS -->
          <div class="absolute top-1 right-1 flex flex-col">
            <button
              type="button"
              class="ot-btn p-1 h-6 w-6 rounded-b-none border border-gray-300 bg-white bg-opacity-80"
              @click="increaseZoom"
            >
              <span class="sr-only">Zoom in</span>
              <font-awesome-icon :icon="['fas', 'plus']" class="" />
            </button>
            <button
              type="button"
              class="ot-btn -mt-px p-1 h-6 w-6 rounded-t-none border border-gray-300 bg-white bg-opacity-80"
              @click="mapZoom--"
            >
              <span class="sr-only">Zoom out</span>
              <font-awesome-icon :icon="['fas', 'minus']" class="" />
            </button>

            <button
              class="ot-btn p-1 h-6 w-6 mt-1 border border-gray-300 bg-white bg-opacity-80"
              @click="zoomMap"
            >
              <font-awesome-icon :icon="['fas', 'expand']" class="" />
            </button>
          </div>
        </div>

        <!-- MAP LEGEND -->
        <div class="mb-2 text-xs flex justify-end">
          <div
            class="flex flex-col sm:flex-row space-x-0 sm:space-x-2 space-y-0.5 sm:space-y-0"
          >
            <div>
              <font-awesome-icon
                :icon="['fas', 'square']"
                :style="{ color: heatmapColorScale(1) }"
              />
              Policy is present
            </div>

            <div>
              <font-awesome-icon
                :icon="['fas', 'square']"
                :style="{ color: heatmapColorScale(0) }"
              />
              Policy is not present
            </div>
            <div>
              <font-awesome-icon
                :icon="['fas', 'square']"
                :style="{ color: colors['na'] }"
              />
              No data available
            </div>
          </div>
        </div>
        <div class="text-right text-xs mb-2">
          Striped countries are not included in this tracker
        </div>

        <div v-for="(section, i) in policy.summary" :key="i" class="mb-6">
          <h3 class="font-bold mb-2">{{ section.title }}</h3>
          <p v-for="(p, i) in section.paragraphs" :key="i" class="mb-2">
            {{ p }}
          </p>
        </div>
        <ul
          v-if="citations && citations.length"
          class="border-t pt-4 text-sm text-gray-500 list-decimal list-outside pl-6"
        >
          <li
            v-for="(citation, i) in citations"
            :key="i"
            :value="citation.id"
            class="mb-2"
            :class="{ hidden: !citation.text }"
          >
            <a
              :href="citation.sources"
              class="hover:underline"
              target="_blank"
              >{{ citation.text }}</a
            >
          </li>
        </ul>
      </div>
    </TwModal>
  </div>
</template>

<script>
import api from "@/services/data";
import * as d3 from "d3";

import { getNumber, getCitationNumbers } from "@/utils/helpers";

import domains from "@/assets/data/domains";
import regions from "@/assets/data/regions";
import countrynames from "@/assets/data/countrynames";

import colors from "@/utils/colors";

import HeatMap from "@/components/HeatMap";
import TwModal from "@/components/TwModal";
import VTooltip from "@/components/VTooltip";
import DownloadButton from "@/components/DownloadButton";
import CountryFlag from "@/components/CountryFlag";
import WorldMap from "@/components/WorldMap";
import SelectCustom from "@/components/SelectCustom";

export default {
  name: "PolicyChecklist",
  components: {
    HeatMap,
    // DomainGrid,
    VTooltip,
    TwModal,
    DownloadButton,
    CountryFlag,
    WorldMap,
    SelectCustom,
  },
  props: {
    domainId: {
      type: String,
    },
  },
  data() {
    return {
      publicPath: window.data_vis_uri || process.env.BASE_URL,
      loading: false,
      data: [],
      globalData: [],
      domains,
      regions: regions.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      }),
      grayColor: colors.gray[200],
      showModal: false,
      policy: null,
      heatmapTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      headerTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      worldMapTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
        iso: null,
      },
      colors: {
        na: colors.gray[300],
        text: colors.policy2,
      },
      policyId: null,
      fitBoundsToData: true,
      mapZoom: 1,
      currentZoom: 1,
      mapColorScale: (d) => {
        switch (d?.value) {
          case 1:
            return colors.policy1;
          case 0:
            return colors.policy0;
        }
        return colors.policy0;
      },
      noColorData: colors.gray[200],
      // policy texts
      texts: {},
    };
  },
  computed: {
    iso() {
      // the order of preference for the country iso is:
      // 1 - URL param
      // 2 - localStorage value
      // 3 - Random
      return this.$route.params.iso || localStorage.getItem("iso") || "CMR";
    },
    domain() {
      return domains.find((e) => e.id === this.domainId);
    },
    regionId() {
      // // first region on the list
      // const regionId = regions[0].id;
      // // the one on the route, or if there's none, the first one on the list of regions
      // return this.$route.params.regionId || regionId;
      return this.$route.params.regionId || "sub-saharan-africa";
    },
    region() {
      return regions.find((e) => e.id === this.regionId);
    },
    filteredData() {
      return this.data.filter((policy) =>
        policy.domain_id
          .trim()
          .split("-")
          .includes(this.domainId)
      );
    },
    heatmapData() {
      return this.filteredData.map((row) => {
        const values = Object.keys(row)
          // remove all columns that are not country data
          .filter(
            (col) =>
              !col.includes("source") &&
              !col.includes("year") &&
              !["policy_id", "domain_id"].includes(col)
          )
          // keep only the countries for the current region (or keep them all)
          .filter(
            (iso) => !this.regionId || this.region.countries.includes(iso)
          )
          .map((iso) => {
            const num = getNumber(row[iso]);
            if (isNaN(num)) return row[iso];
            return getNumber(row[iso]);
          });

        return {
          id: row["policy_id"],
          name:
            this.texts[row["policy_id"]]?.name ||
            `${row["policy_id"]} (missing policy text)`,
          values,
        };
      });
    },
    heatmapCategories() {
      if (!this.filteredData[0]) return [];

      return (
        Object.keys(this.filteredData[0])
          // remove all columns that are not country data
          .filter(
            (col) =>
              !col.includes("source") &&
              !col.includes("year") &&
              !["policy_id", "domain_id"].includes(col)
          )
          // keep only the countries for the current region (or keep them all)
          .filter(
            (iso) => !this.regionId || this.region.countries.includes(iso)
          )
          .map((iso) => ({
            id: iso,
            name: countrynames[iso] || iso,
          }))
      );
    },
    heatmapColorScale() {
      return d3
        .scaleQuantize()
        .domain([0, 1])
        .range([colors.policy0, colors.policy1])
        .unknown(this.colors.na);
    },
    policyDataLink() {
      return `/data/Policy_checklist_data.xlsx`;
    },
    mapData() {
      const result = {};
      const row =
        this.filteredData?.find((e) => e.policy_id === (this.policyId || 2)) ||
        {};

      const values = Object.keys(row).filter(
        (col) =>
          !col.includes("source") &&
          !col.includes("year") &&
          !["policy_id", "domain_id"].includes(col)
      );

      values.forEach((iso) => {
        result[iso] = {
          value: +row[iso],
        };
      });
      return result;
    },
    citations() {
      const result = [];
      this.policy?.summary.forEach((section) =>
        section.paragraphs.forEach((p) => {
          result.push(getCitationNumbers(p));
        })
      );
      return result.flat().map((e) => ({ ...this.citationTexts[e], id: e }));
    },
    domainsOptions() {
      return [{ name: "-- SELECT DOMAIN --", id: "" }, ...domains];
    },
  },
  mounted() {
    this.loadData();
    this.loadGlobalData();
    this.loadPolicyTexts();
    this.loadPolicyCitationTexts();
    this.initial();
  },
  beforeDestroy() {
    this.final();
  },
  methods: {
    async loadData() {
      try {
        this.loading = true;
        this.data = await api.getPolicyData();
      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async loadGlobalData() {
      try {
        this.globalData = await api.getPopulations();
      } catch (err) {
        this.error = true;
      }
    },
    async loadPolicyTexts() {
      try {
        this.texts = await api.getPolicyTexts();
      } catch (err) {
        this.error = true;
      }
    },
    async loadPolicyCitationTexts() {
      try {
        this.citationTexts = await api.getPolicyCitationTexts();
      } catch (err) {
        this.error = true;
      }
    },
    routeRegion(regionId) {
      this.route({ regionId });
    },
    routeDomain(domainId) {
      // const domainId = event.target.value;
      // find the first indicator of this domain
      const indicatorId = domains.find((e) => e.id === domainId)?.indicators[0]
        ?.id;

      // if back to homepage, remove the ISO from the URL
      if (!domainId) this.route({ domainId, indicatorId, iso: "" });
      // reset the indicatorId when changing the domain
      else this.route({ domainId, indicatorId, iso: this.iso });
    },
    route(params) {
      const query = { ...this.$route.params, ...params };

      for (const key in query) {
        if (Object.hasOwnProperty.call(query, key)) {
          if (!query[key]) delete query[key];
        }
      }

      this.$router.push({ name: "policy", params: query });
    },
    showDetails(policy) {
      this.policyId = policy.id;
      this.policy = this.texts[policy.id];
      this.showModal = true;
    },
    showHeatmapTooltip({ event, data, i }) {
      this.heatmapTooltip.target = event.target;

      const value = data.values[i];
      let text;

      if (value === 1) text = "Policy is present";
      else if (value === "n/a") text = "No data available";
      else if (value === 0) text = "Policy is not present";
      else text = value;

      this.heatmapTooltip.value = text;
      this.heatmapTooltip.subtitle = data.name;
      this.heatmapTooltip.title = countrynames[this.heatmapCategories[i].id];
      this.heatmapTooltip.show = true;
      this.heatmapTooltip.iso = this.heatmapCategories[i].id;
    },
    showHeaderTooltip(payload) {
      if (!payload) this.headerTooltip.show = false;
      else {
        this.headerTooltip.target = payload.event.target;
        this.headerTooltip.title = countrynames[payload.data];
        this.headerTooltip.iso = payload.data;
        // OVERRIDE THIS: we don't want to show the tooltip anymore
        this.headerTooltip.show = false;
      }
    },
    hideHeatmapTooltip() {
      this.heatmapTooltip.show = false;
    },
    setCurrentZoom(transform) {
      this.currentZoom = transform.k;
    },
    increaseZoom() {
      this.mapZoom++; // = parseInt(this.currentZoom) + 1
    },
    zoomMap() {
      this.$refs.worldMap.zoomToFitBounds();
    },
    toggleTooltip(info) {
      if (!info) this.worldMapTooltip.show = false;
      else {
        const { data, event } = info;

        // show tooltip only if we have data
        if (this.mapData[data.id]) {
          const { value } = data;
          this.worldMapTooltip.target = event.target;

          this.worldMapTooltip.title = countrynames[data.id];

          let text = "";
          if (value === 1) text = "Policy is present";
          else if (value === "n/a" || isNaN(value)) text = "No data available";
          else if (value === 0) text = "Policy is not present";
          else text = value;

          this.worldMapTooltip.value = text;

          this.worldMapTooltip.number = this.globalData.find(
            (e) => e.id === data.id
          )?.total;

          this.worldMapTooltip.iso = data.id;
          this.worldMapTooltip.show = true;
        }
      }
    },

    // ANIMATION
    initial() {
      // enter();
    },
    final() {
      // leave(false);
    },
  },
};
</script>
